// Utility functions for handling dynamic theming in public booking pages
import { getColorValue, getContrastColor } from './colorUtils';

// Tailwind CSS Safelist - DO NOT DELETE
// These classes are used dynamically and need to be preserved in builds:
// bg-ink-800 bg-gray-700 bg-gray-600 bg-gray-500 bg-cream-100 bg-white
// text-gray-50 text-ink-300 text-ink-900 text-ink-600 text-white
// border-gray-600 border-gray-500 border-ink-300 border-ink-200
// Primary color classes for radio buttons and selections:
// bg-accent-600 bg-emerald-600 bg-violet-600 bg-pink-600 bg-signal-red-600 bg-gray-600
// border-accent-600 border-emerald-600 border-violet-600 border-pink-600 border-red-600 border-gray-600
// Light selection state classes:
// bg-blue-200 bg-emerald-200 bg-violet-200 bg-pink-200 bg-red-200 bg-cream-300
// text-blue-900 text-emerald-900 text-violet-900 text-pink-900 text-red-900 text-ink-900
// border-blue-300 border-emerald-300 border-violet-300 border-pink-300 border-signal-red-100 border-ink-200
// Badge classes:
// bg-accent-100 bg-emerald-100 bg-violet-100 bg-pink-100 bg-signal-red-100 bg-cream-200
// text-accent-700 text-emerald-800 text-violet-800 text-pink-800 text-signal-red-700 text-ink-800
// border-accent-200 border-emerald-200 border-violet-200 border-pink-200 border-signal-red-100 border-ink-100
// Dark mode improved visibility classes:
// bg-gray-500 text-white border-ink-300
// Request Custom Time button classes:
// border-accent-600 border-emerald-600 border-violet-600 border-pink-600 border-red-600 border-gray-600
// text-accent-600 text-emerald-600 text-violet-600 text-pink-600 text-signal-red-600 text-ink-600
// hover:bg-accent-100 hover:bg-emerald-50 hover:bg-violet-50 hover:bg-pink-50 hover:bg-signal-red-50 hover:bg-cream-100

// Extract theme configuration from bookingPage object with fallbacks
export const getBookingPageTheme = (bookingPage) => {
  return {
    primaryColor: bookingPage?.primaryColor || 'blue-600',
    themeMode: bookingPage?.themeMode || 'light'
  };
};


// Helper function to calculate shade variants
const calculateShadeVariants = (baseShade) => ({
  hover: Math.min(baseShade + 100, 900),
  focus: Math.max(baseShade - 100, 300),
  background: Math.max(baseShade - 450, 50),
  border: Math.max(baseShade - 400, 200)
});

// Helper function to parse color and generate variants
const parseColorAndGenerateVariants = (color) => {
  const [colorName, shade] = color.split('-');
  const shadeNum = parseInt(shade);
  const variants = calculateShadeVariants(shadeNum);
  
  return {
    colorName,
    shadeNum,
    hoverColor: `${colorName}-${variants.hover}`,
    focusColor: `${colorName}-${variants.focus}`,
    bgColor: `${colorName}-${variants.background}`,
    borderColor: `${colorName}-${variants.border}`
  };
};

// Generate theme classes dynamically based on color value
export const getThemeColorClasses = (color = 'blue-600') => {
  const colorValue = getColorValue(color);
  const contrastColor = getContrastColor(colorValue);
  const { hoverColor, focusColor, bgColor, borderColor } = parseColorAndGenerateVariants(color);
  
  return {
    primary: `bg-${color} text-${contrastColor === 'white' ? 'white' : 'black'} border-${color}`,
    primaryHover: `hover:bg-${hoverColor} hover:border-${hoverColor}`,
    primaryFocus: `focus:ring-${focusColor} focus:border-${focusColor}`,
    primaryText: `text-${color}`,
    primaryBg: `bg-${bgColor}`,
    primaryBorder: `border-${borderColor}`,
    colorValue,
    contrastColor
  };
};


export const getThemedButtonClasses = (primaryColor = 'blue-600') => {
  const theme = getThemeColorClasses(primaryColor);
  return `${theme.primary} ${theme.primaryHover} ${theme.primaryFocus} rounded-md px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors`;
};

export const getThemedAvatarClasses = (primaryColor = 'blue-600') => {
  const theme = getThemeColorClasses(primaryColor);
  // For avatar, we'll use the primary color as background
  return theme.primary.replace('border-', 'bg-').replace('text-white', 'text-white');
};

// Dark theme detection - now based on themeMode
export const isDarkTheme = (themeMode = 'light', primaryColor = 'blue-600') => {
  return themeMode === 'dark';
};

// Get themed background classes - updated to use themeMode
export const getThemedBackgroundClasses = (primaryColor = 'blue-600', themeMode = 'light') => {
  const darkMode = isDarkTheme(themeMode);
  
  return {
    pageBackground: darkMode ? 'bg-ink-800' : 'bg-cream-100',
    cardBackground: darkMode ? 'bg-gray-700' : 'bg-white',
    headerBackground: darkMode ? 'bg-gray-700' : 'bg-white',
    textPrimary: darkMode ? 'text-gray-50' : 'text-ink-900',
    textSecondary: darkMode ? 'text-ink-300' : 'text-ink-600',
    border: darkMode ? 'border-gray-600' : 'border-ink-200',
    // Color values for inline styles
    pageBackgroundColor: darkMode ? '#1f2937' : '#f9fafb',
    cardBackgroundColor: darkMode ? '#374151' : '#ffffff',
    headerBackgroundColor: darkMode ? '#374151' : '#ffffff',
    textPrimaryColor: darkMode ? '#f9fafb' : '#111827',
    textSecondaryColor: darkMode ? '#d1d5db' : '#4b5563',
    borderColor: darkMode ? '#4b5563' : '#d1d5db',
    isDark: darkMode
  };
};




// Dynamic theme classes generator - replaces hardcoded color maps
export const getDynamicThemeClasses = (primaryColor = 'blue-600', themeMode = 'light') => {
  const darkMode = isDarkTheme(themeMode);
  const primaryColorValue = getColorValue(primaryColor);
  const contrastColor = getContrastColor(primaryColorValue);
  
  return {
    // Button styles
    primaryButton: `bg-${primaryColor} text-${contrastColor === 'white' ? 'white' : 'black'} border-${primaryColor} hover:opacity-90 focus:ring-${primaryColor} focus:ring-opacity-50`,
    secondaryButton: `border-${primaryColor} text-${primaryColor} bg-transparent hover:bg-${primaryColor} hover:bg-opacity-10 focus:ring-${primaryColor} focus:ring-opacity-50`,
    
    // Form elements
    inputFocus: `focus:outline-${primaryColor} focus:ring-${primaryColor}`,
    checkboxChecked: `checked:bg-${primaryColor} checked:border-${primaryColor}`,
    radioChecked: `checked:bg-${primaryColor} checked:border-${primaryColor}`,
    
    // Interactive states
    selectedState: `bg-${primaryColor} text-${contrastColor === 'white' ? 'white' : 'black'} border-${primaryColor}`,
    hoverState: `hover:bg-${primaryColor} hover:bg-opacity-10`,
    focusState: `focus:ring-2 focus:ring-${primaryColor} focus:ring-opacity-50`,
    
    // Loading elements
    spinner: `border-${primaryColor}`,
    
    // Theme-based backgrounds and text (static classes for Tailwind detection)
    background: darkMode ? 'bg-ink-800' : 'bg-cream-100',
    cardBackground: darkMode ? 'bg-gray-700' : 'bg-white',
    textPrimary: darkMode ? 'text-gray-50' : 'text-ink-900',
    textSecondary: darkMode ? 'text-ink-300' : 'text-ink-600',
    border: darkMode ? 'border-gray-600' : 'border-ink-200',
  };
};

